import { useEffect, useRef } from 'react';

/**
 * Custom hook for smooth auto-play using requestAnimationFrame instead of setInterval
 * This reduces main thread blocking and provides smoother animations with better performance
 * 
 * @param callback - Function to call on each animation frame
 * @param delay - Delay in milliseconds between calls
 * @param enabled - Whether auto-play is enabled
 */
export function useRequestAnimationFrame(
    callback: () => void,
    delay: number,
    enabled: boolean = true
) {
    const savedCallback = useRef(callback);
    const requestRef = useRef<number | undefined>(undefined);
    const previousTimeRef = useRef<number | undefined>(undefined);
    const delayRef = useRef(delay);

    // Remember the latest callback and delay
    useEffect(() => {
        savedCallback.current = callback;
        delayRef.current = delay;
    }, [callback, delay]);

    useEffect(() => {
        if (!enabled) {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            return;
        }

        // Animation loop
        const animate = (time: number) => {
            if (previousTimeRef.current !== undefined) {
                const deltaTime = time - previousTimeRef.current;

                if (deltaTime >= delayRef.current) {
                    savedCallback.current();
                    previousTimeRef.current = time;
                }
            } else {
                previousTimeRef.current = time;
            }

            requestRef.current = requestAnimationFrame(animate);
        };

        requestRef.current = requestAnimationFrame(animate);

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, [enabled]);
}
